// Global singleton WebSocket for Strategy Engine signals. JWT cookie auth,
// exponential-backoff reconnect (1s→30s), stops on 4401, separate message vs
// status listeners. Opened once in App.js.

import { STRATEGY_ENGINE_URL } from '../services/strategyEngineApi';

// ── Internal state ──────────────────────────────────────────────────────────

let ws = null;
let intentionalClose = false;
let reconnectTimer = null;
let reconnectDelay = 1000;                // starts at 1 s
const MAX_RECONNECT_DELAY = 30_000;       // caps at 30 s
const RECONNECT_JITTER = 0.3;            // ±30 % jitter

const messageListeners = new Set();
const statusListeners = new Set();

// ── URL builder ─────────────────────────────────────────────────────────────

function buildWsUrl() {
  // STRATEGY_ENGINE_URL is e.g. "http://localhost:3003/"
  // Replace the scheme and append the /ws path.
  const base = STRATEGY_ENGINE_URL.replace(/^http/, 'ws');
  // The backend mounts the WS server on path "/ws"  (signalWs.js line 31).
  // STRATEGY_ENGINE_URL already ends with "/" so base + "ws" → ".../ws".
  return `${base}ws`;
}

// ── Public API ──────────────────────────────────────────────────────────────

// Open the signal WebSocket (no-op if already open/connecting). Only connects
// when a userSession exists; a later call after login will succeed.
export function connectSignalWS() {
  // Already open or in-progress — nothing to do.
  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  // Only connect when logged in; without a session the backend closes 4401.
  const session = localStorage.getItem('userSession');
  if (!session) return;

  intentionalClose = false;

  try {
    const url = buildWsUrl();
    ws = new WebSocket(url);

    ws.onopen = () => {
      // Reset backoff on success so the next failure starts fresh.
      reconnectDelay = 1000;
      _notifyStatus(true);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        messageListeners.forEach((fn) => {
          try { fn(msg); } catch (_) { /* listener errors must never kill the loop */ }
        });
      } catch (_) {
        // Non-JSON frame — ignore (e.g. a ping/pong text frame).
      }
    };

    ws.onclose = (event) => {
      ws = null;
      _notifyStatus(false);

      // 4401 = "Authentication required" — the JWT is missing or invalid.
      // Reconnecting would be futile until the user logs in again, so we
      // stop the backoff loop entirely.
      if (intentionalClose || event.code === 4401) return;

      _scheduleReconnect();
    };

    ws.onerror = () => {
      // The browser fires `onerror` then immediately fires `onclose`,
      // so the reconnect logic is handled in `onclose` above.
    };
  } catch (_) {
    // WebSocket constructor threw (e.g. invalid URL).
    _scheduleReconnect();
  }
}

/**
 * Gracefully close the signal WebSocket.
 * Stops the reconnect timer so we don't re-open after an intentional close
 * (e.g. user logs out).
 */
export function disconnectSignalWS() {
  intentionalClose = true;

  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  if (ws) {
    // Prevent the onclose handler from scheduling a reconnect.
    ws.onclose = null;
    ws.close();
    ws = null;
  }

  _notifyStatus(false);
}

// Subscribe to signal messages (msg.type 'NEW_SIGNAL' | 'CONNECTED').
// Returns an unsubscribe function.
export function onSignalMessage(fn) {
  messageListeners.add(fn);
  return () => messageListeners.delete(fn);
}

// Subscribe to connection-status changes. Returns an unsubscribe function.
export function onSignalStatusChange(fn) {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}

// Current connection state.
export function isSignalWsConnected() {
  return ws !== null && ws.readyState === WebSocket.OPEN;
}

// ── Internals ───────────────────────────────────────────────────────────────

function _scheduleReconnect() {
  if (reconnectTimer) return; // already scheduled

  // Exponential backoff with jitter to avoid thundering-herd reconnect
  // storms when many admin users are online simultaneously.
  const jitter = 1 - RECONNECT_JITTER + Math.random() * (RECONNECT_JITTER * 2);
  const delay = Math.min(reconnectDelay * jitter, MAX_RECONNECT_DELAY);

  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
    connectSignalWS();
  }, delay);
}

function _notifyStatus(connected) {
  statusListeners.forEach((fn) => {
    try { fn(connected); } catch (_) { /* never propagate listener errors */ }
  });
}
